<template>
  <div class="msg" data-test="pfe-tab-message-body">
    <div class="msg-left">
      <!-- WHEN TO SEND -->
      <section class="msg-card" aria-labelledby="pfe-when-title">
        <div class="msg-card-title"><i class="fa fa-clock-o" aria-hidden="true"></i><span id="pfe-when-title">When to send</span></div>

        <div class="msg-line">
          <span>Send</span>
          <input
            v-model="delayValue"
            type="number"
            min="0"
            step="any"
            class="msg-num"
            :class="{ 'msg-field--bad': errors.delayMinutes }"
            aria-label="Delay"
            data-test="pfe-delay"
            @input="onDelay"
          />
          <select v-model="delayUnit" class="msg-select msg-select--unit" aria-label="Unit" data-test="pfe-delay-unit" @change="onUnit">
            <option v-for="u in units" :key="u" :value="u">{{ u }}</option>
          </select>
          <span>after the procedure is marked complete.</span>
        </div>
        <div v-if="errors.delayMinutes" class="msg-error" role="alert" data-test="pfe-error-delay">
          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ errors.delayMinutes }}
        </div>

        <div class="msg-line">
          <span>Only between</span>
          <select :value="draft.quietStart" class="msg-select" :class="{ 'msg-field--bad': errors.window }" aria-label="Earliest time" data-test="pfe-window-from" @change="onWindow('quietStart', $event.target.value)">
            <option v-for="o in timeOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
          </select>
          <span>and</span>
          <select :value="draft.quietEnd" class="msg-select" :class="{ 'msg-field--bad': errors.window }" aria-label="Latest time" data-test="pfe-window-to" @change="onWindow('quietEnd', $event.target.value)">
            <option v-for="o in timeOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
          </select>
          <span>&mdash; outside these hours we send the next morning.</span>
        </div>
        <div v-if="errors.window" class="msg-error" role="alert" data-test="pfe-error-window">
          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ errors.window }}
        </div>

        <p class="msg-help">
          One message per visit: if a patient has an extraction and a bone graft, they get a single message with both
          instructions. Procedures completed on a past date are never sent.
        </p>
      </section>

      <!-- THE MESSAGE -->
      <section class="msg-card" aria-labelledby="pfe-message-title">
        <div class="msg-card-head">
          <div class="msg-card-headtext">
            <div class="msg-card-title msg-card-title--plain" id="pfe-message-title">Message</div>
            <div class="msg-sub">One text and one email, shared by every post-op form.</div>
          </div>
          <div class="msg-pills" role="tablist" aria-label="Message type">
            <button type="button" role="tab" class="msg-pill" :class="{ 'msg-pill--on': channel === 'text' }" :aria-selected="channel === 'text' ? 'true' : 'false'" data-test="pfe-channel-text" @click="channel = 'text'">Text</button>
            <button type="button" role="tab" class="msg-pill" :class="{ 'msg-pill--on': channel === 'email' }" :aria-selected="channel === 'email' ? 'true' : 'false'" data-test="pfe-channel-email-tab" @click="channel = 'email'">Email</button>
          </div>
        </div>

        <div v-if="channel === 'text'" class="msg-stack">
          <label class="msg-label" for="pfe-sms-body">Text message</label>
          <textarea
            id="pfe-sms-body"
            ref="smsBox"
            class="msg-area"
            :class="{ 'msg-field--bad': errors['sms.body'] }"
            rows="6"
            :value="draft.sms.body"
            :aria-invalid="errors['sms.body'] ? 'true' : 'false'"
            data-test="pfe-sms-body"
            @focus="touched.smsBox = true"
            @input="onSms($event.target.value)"
          ></textarea>
          <div v-if="errors['sms.body']" class="msg-error" role="alert" data-test="pfe-error-sms">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ errors['sms.body'] }}
          </div>
          <div class="msg-chiprow">
            <div class="msg-chips">
              <button
                v-for="c in smsChips"
                :key="c.token"
                type="button"
                class="msg-chip"
                :class="chipClass(c, draft.sms.body)"
                :title="c.token"
                data-test="pfe-chip"
                :data-token="c.token"
                @click="insert(c.token)"
              ><i class="fa fa-plus" aria-hidden="true"></i>{{ c.label }}<span v-if="c.required" class="msg-chip-req">&middot; required</span></button>
            </div>
            <span class="msg-counter" data-test="pfe-counter">{{ counterText }}</span>
          </div>
        </div>

        <div v-else class="msg-stack">
          <label class="msg-label" for="pfe-email-subject">Subject</label>
          <input
            id="pfe-email-subject"
            ref="subjectBox"
            type="text"
            class="msg-input"
            :class="{ 'msg-field--bad': errors['email.subject'] }"
            :value="draft.email.subject"
            data-test="pfe-email-subject"
            @focus="focusField('subjectBox', 'subject')"
            @input="onSubject($event.target.value)"
          />
          <div v-if="errors['email.subject']" class="msg-error" role="alert" data-test="pfe-error-subject">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ errors['email.subject'] }}
          </div>
          <label class="msg-label" for="pfe-email-body">Email body</label>
          <textarea
            id="pfe-email-body"
            ref="bodyBox"
            class="msg-area"
            :class="{ 'msg-field--bad': errors['email.body'] }"
            rows="11"
            :value="draft.email.body"
            :aria-invalid="errors['email.body'] ? 'true' : 'false'"
            data-test="pfe-email-body"
            @focus="focusField('bodyBox', 'body')"
            @input="onBody($event.target.value)"
          ></textarea>
          <div v-if="errors['email.body']" class="msg-error" role="alert" data-test="pfe-error-email-body">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ errors['email.body'] }}
          </div>
          <div class="msg-chips">
            <button
              v-for="c in emailChips"
              :key="c.token"
              type="button"
              class="msg-chip"
              :class="chipClass(c, emailText)"
              :title="c.token"
              data-test="pfe-chip"
              :data-token="c.token"
              @click="insert(c.token)"
            ><i class="fa fa-plus" aria-hidden="true"></i>{{ c.label }}<span v-if="c.required" class="msg-chip-req">&middot; required</span></button>
          </div>
        </div>

        <div class="msg-reset">
          <button type="button" class="msg-link" data-test="pfe-reset-message" @click="resetDefault">
            <i class="fa fa-undo" aria-hidden="true"></i>Reset to default message
          </button>
        </div>
      </section>
    </div>

    <!-- PREVIEW -->
    <aside class="msg-right" data-test="pfe-preview" aria-label="Preview">
      <div class="msg-prev-head">
        <span class="msg-prev-title">Preview</span>
        <span class="msg-prev-with">Preview with:</span>
        <div class="msg-pills" role="tablist" aria-label="Number of procedures">
          <button type="button" role="tab" class="msg-pill" :class="{ 'msg-pill--on': count === 1 }" :aria-selected="count === 1 ? 'true' : 'false'" data-test="pfe-preview-count-1" @click="count = 1">1 procedure</button>
          <button type="button" role="tab" class="msg-pill" :class="{ 'msg-pill--on': count === 2 }" :aria-selected="count === 2 ? 'true' : 'false'" data-test="pfe-preview-count-2" @click="count = 2">2 procedures</button>
        </div>
      </div>
      <div class="msg-prev-sample" data-test="pfe-preview-sample">Sample patient: Maria Lopez &middot; {{ procedureText }}</div>
      <message-preview :channel="channel" :count="count" :sms-body="draft.sms.body" :email="draft.email" :site="site" />
    </aside>
  </div>
</template>

<script>
import MessagePreview from './MessagePreview.vue';
import { spliceAtCursor } from '@/utils/textInsert';
import { analyzeSms } from '@/utils/smsSegments';
import {
  POST_OP_DEFAULTS, SMS_CHIPS, EMAIL_CHIPS, DELAY_UNITS, delayToUnit, unitToMinutes, timeOptionsWith, hasToken, hasPostOpLinks,
  fillPreviewText, smsCounterText, previewProcedureText,
} from '@/utils/postOp/message';

/**
 * The "Post-op message" tab: when to send, the ONE shared text + ONE shared email (edited in place, with variable chips and
 * a segment counter), and the live preview. Presentational + local UI state: the editor owns the draft and validates it
 * (utils/postOp/message.js validateMessageDraft); this component emits the next whole draft as `change`.
 * Chip tokens are the REAL ones ({{site_name}}, {{site_officephone}}, {{email_signature_block}}…), not the design file's.
 */
export default {
  name: 'PfeMessageTab',
  components: { MessagePreview },
  props: {
    /** { delayMinutes, quietStart, quietEnd, sms:{ body }, email:{ subject, body } } */
    draft: { type: Object, required: true },
    /** field → message (validateMessageDraft + any server-side 422 problems) */
    errors: { type: Object, default: () => ({}) },
    /** { name, phone, senderEmail, signatureEnabled, signatureHtml } — the REAL practice, for the preview */
    site: { type: Object, default: () => ({}) },
  },
  data() {
    // a half-typed (invalid) delay survives a tab switch as a blank box with its error, not as a made-up "1 hour"
    const d = Number.isInteger(this.draft.delayMinutes) ? delayToUnit(this.draft.delayMinutes) : { value: '', unit: 'minutes' };
    return {
      channel: 'text',
      count: 1,
      lastEmailField: 'body',
      delayValue: String(d.value),
      delayUnit: d.unit,
      prevUnit: d.unit,
      units: DELAY_UNITS,
      touched: { smsBox: false, subjectBox: false, bodyBox: false },
    };
  },
  computed: {
    smsChips() { return SMS_CHIPS; },
    emailChips() { return EMAIL_CHIPS; },
    emailText() { return `${this.draft.email.subject}\n${this.draft.email.body}`; },
    timeOptions() { return timeOptionsWith(this.draft.quietStart, this.draft.quietEnd); },
    procedureText() { return previewProcedureText(this.count); },
    counterText() {
      const a = analyzeSms(fillPreviewText(this.draft.sms.body, this.site, this.count));
      return smsCounterText(a.length, a.segments);
    },
  },
  watch: {
    // Reset / reload hands us a different delay: re-seed the number + unit. NaN (a half-typed value) is skipped.
    'draft.delayMinutes'(minutes) {
      if (!Number.isInteger(minutes)) return;
      if (unitToMinutes(this.delayValue, this.delayUnit) === minutes) return;
      const d = delayToUnit(minutes);
      this.delayValue = String(d.value);
      this.delayUnit = d.unit;
      this.prevUnit = d.unit;
    },
  },
  methods: {
    emit(patch) { this.$emit('change', Object.assign({}, this.draft, patch)); },
    onDelay() { this.emit({ delayMinutes: unitToMinutes(this.delayValue, this.delayUnit) }); },
    onUnit() {
      // v-model has already moved delayUnit to the NEW unit. Keep the SAME duration where it can (60 minutes → 1 hour),
      // instead of silently turning 60 minutes into 60 hours.
      const from = this.prevUnit;
      const to = this.delayUnit;
      if (from !== to) {
        const minutes = unitToMinutes(this.delayValue, from);
        if (Number.isFinite(minutes)) this.delayValue = String(to === 'hours' ? minutes / 60 : minutes);
      }
      this.prevUnit = to;
      this.onDelay();
    },
    onWindow(key, value) { this.emit({ [key]: value }); },
    onSms(body) { this.emit({ sms: { body } }); },
    onSubject(subject) { this.emit({ email: Object.assign({}, this.draft.email, { subject }) }); },
    onBody(body) { this.emit({ email: Object.assign({}, this.draft.email, { body }) }); },
    focusField(ref, which) {
      this.touched[ref] = true;
      this.lastEmailField = which;
    },
    chipClass(chip, text) {
      const present = hasToken(text, chip.token);
      return { 'msg-chip--in': present, 'msg-chip--missing': !!chip.required && !hasPostOpLinks(text) };
    },
    /** Drop a variable at the caret of the box being edited (the email's subject or body, whichever was focused last). */
    insert(token) {
      let ref;
      let current;
      let build;
      if (this.channel === 'text') {
        ref = 'smsBox'; current = this.draft.sms.body; build = (v) => this.onSms(v);
      } else if (this.lastEmailField === 'subject') {
        ref = 'subjectBox'; current = this.draft.email.subject; build = (v) => this.onSubject(v);
      } else {
        ref = 'bodyBox'; current = this.draft.email.body; build = (v) => this.onBody(v);
      }
      const el = this.$refs[ref];
      const useCaret = el && this.touched[ref];
      const { text, caret } = spliceAtCursor(current, useCaret ? el.selectionStart : undefined, useCaret ? el.selectionEnd : undefined, token);
      build(text);
      this.$nextTick(() => {
        if (!el) return;
        el.focus();
        if (typeof el.setSelectionRange === 'function') el.setSelectionRange(caret, caret);
      });
    },
    resetDefault() {
      if (this.channel === 'text') {
        this.onSms(POST_OP_DEFAULTS.sms.body);
      } else {
        this.emit({ email: { subject: POST_OP_DEFAULTS.email.subject, body: POST_OP_DEFAULTS.email.body } });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.msg {
  max-width: 1320px;
  margin: 0 auto;
  padding: 22px 32px 40px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}
.msg-left { flex: 1 1 540px; min-width: 0; display: flex; flex-direction: column; gap: 16px; }
.msg-right {
  flex: 1 1 420px;
  max-width: 600px;
  min-width: 0;
  position: sticky;
  top: 0;
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.msg-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.msg-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: -0.01em;

  i { color: $pfe-neutral-500; font-size: 16px; }
  &--plain { display: block; }
}
.msg-card-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.msg-card-headtext { flex: 1; min-width: 200px; }
.msg-sub { margin-top: 3px; font-size: 13px; color: $pfe-neutral-500; }

.msg-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 14.5px;
}
.msg-num, .msg-select, .msg-input {
  @include pfe-field(40px);
  height: 40px;
  padding: 0 10px;

  &:focus { @include pfe-focus-ring; }
}
.msg-num { width: 84px; font-variant-numeric: tabular-nums; }
.msg-select { min-width: 130px; }
.msg-select--unit { min-width: 120px; }
.msg-input { width: 100%; font-size: 14.5px; }
.msg-field--bad { border-color: #d9534f !important; }
.msg-help {
  margin: 0;
  padding-top: 12px;
  border-top: 1px solid $pp-border;
  font-size: 13px;
  line-height: 1.55;
  color: $pfe-neutral-500;
}

.msg-pills { display: inline-flex; padding: 3px; border-radius: 999px; background: $pfe-neutral-100; gap: 2px; }
.msg-pill {
  height: 30px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: $pfe-neutral-600;
  cursor: pointer;

  &--on { background: $pp-surface; color: $pp-text; box-shadow: $pp-shadow-raised; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }
}

.msg-stack { display: flex; flex-direction: column; gap: 10px; }
.msg-label { margin: 0; font-size: 13px; font-weight: 700; }
.msg-area {
  @include pfe-field(40px);
  width: 100%;
  resize: vertical;
  padding: 12px 14px;
  font-size: 14.5px;
  line-height: 1.55;

  &:focus { @include pfe-focus-ring; }
}
.msg-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: $pfe-red-ink;
}
.msg-chiprow { display: flex; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.msg-chips { flex: 1; min-width: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.msg-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 11px 0 8px;
  border: 1px solid $pp-border;
  border-radius: 999px;
  background: $pp-surface;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: $pp-text;
  white-space: nowrap;
  cursor: pointer;

  i { font-size: 10px; }
  &:hover { border-color: $pp-border-strong; }
  &--in { color: $pfe-neutral-500; }
  &--missing { border-color: #e5a3a5; background: $pp-red-subtle; }
}
.msg-chip-req { font-weight: 500; color: $pfe-neutral-500; .msg-chip--missing & { color: $pfe-red-ink; } }
.msg-counter { font-size: 13px; color: $pfe-neutral-500; font-variant-numeric: tabular-nums; white-space: nowrap; padding-top: 6px; }
.msg-reset { padding-top: 12px; border-top: 1px solid $pp-border; }
.msg-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: $pp-brand;
  cursor: pointer;

  &:hover { text-decoration: underline; }
}

.msg-prev-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.msg-prev-title { flex: 1; font-family: $pp-font-display; font-weight: 800; font-size: 18px; letter-spacing: -0.01em; }
.msg-prev-with { font-size: 13px; color: $pfe-neutral-500; }
.msg-prev-sample { margin-top: -6px; font-size: 13px; color: $pfe-neutral-500; }

// Narrow screens: the preview stacks under the editor instead of sitting beside it.
@media (max-width: 1100px) {
  .msg-right { position: static; max-width: none; flex-basis: 100%; }
}
@media (max-width: 700px) {
  .msg { padding: 18px 16px 32px; }
}
</style>
